import {
  ArrowLittleDownIcon,
  CheckIcon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@joinrepublic/dls-ui-kit';

import { SORT_BY_OPTIONS } from './constants';
import { ISortByMenuProps, ISortByOption, ISortByValue } from './types';

const findOption = (value: ISortByValue): ISortByOption =>
  SORT_BY_OPTIONS.find((option) => option.value === value) ??
  SORT_BY_OPTIONS[0];

/**
 * Sort control for the growth-stage ventures list. Controlled rather than
 * self-owning: the selection lives in `?sort=` (see `useSortQueryParam`), and the
 * router can change it without remounting this menu — Back/Forward, or the
 * cleanup that strips an unrecognized param — so holding a copy here would let
 * the trigger label drift from the URL.
 *
 * The caller maps the reported value onto the query's sort enum (see
 * `to-offering-search-sort`).
 */
export const SortByMenu = ({ value, onChange }: ISortByMenuProps) => {
  const selected = findOption(value);

  return (
    <Menu placement="bottom-start" isLazy>
      {/* `flexShrink` keeps this label whole when the sector trigger beside it
          holds a long sector name — that one truncates instead. */}
      <MenuButton
        type="button"
        display="inline-flex"
        alignItems="center"
        gap="1"
        flexShrink={0}
        aria-label={`Sort by: ${selected.label}`}
        data-testid="sort-by-trigger"
      >
        <Text
          as="span"
          size="body-sm-md"
          variant="text-secondary"
          whiteSpace="nowrap"
        >
          <Text
            as="span"
            variant="text-secondary"
            whiteSpace="nowrap"
            size="body-sm-md"
            display={{ base: 'none', md: 'inline-block' }}
            marginInlineEnd="1"
          >
            Sort by:
          </Text>
          {selected.label}
        </Text>
        <ArrowLittleDownIcon boxSize="5" color="neutral.fg-secondary" />
      </MenuButton>

      <MenuList>
        {SORT_BY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => onChange(option.value)}
            data-testid="sort-by-option"
            data-sort-value={option.value}
          >
            {option.label}
            {option.value === selected.value && <CheckIcon ml="auto" />}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
